import React, { useState } from "react";
import { useLocationAnalytics } from "../../../hooks/useDemandAnalytics";
import HorizontalBar from "../../../components/admin/HorizontalBar";
import StatCard from "../../../components/admin/StatCard";

const LocationAnalyticsTab = () => {
  const [days, setDays] = useState(90);
  const { data, isLoading } = useLocationAnalytics(days);

  if (isLoading) {
    return <div className="skeleton h-64 rounded-lg"></div>;
  }

  const d = data?.data;
  if (!d) return <div className="text-gray-500">No data available</div>;

  const totalDonors = (d.donorsByDistrict || []).reduce((s, i) => s + i.count, 0);
  const totalRequests = (d.requestsByDistrict || []).reduce((s, i) => s + i.count, 0);
  const topDistrict = d.donorsByDistrict?.[0]?.district || "N/A";
  const topRequestDistrict = d.requestsByDistrict?.[0]?.district || "N/A";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Period:</span>
        <select
          className="select select-bordered select-sm"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 6 months</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Donors"
          value={totalDonors.toLocaleString()}
          icon="fa-users"
          color="bg-blue-50"
          textColor="text-blue-600"
        />
        <StatCard
          title="Total Requests"
          value={totalRequests.toLocaleString()}
          icon="fa-hand-holding-droplet"
          color="bg-red-50"
          textColor="text-red-600"
        />
        <StatCard
          title="Top Donor District"
          value={topDistrict}
          icon="fa-map-marker"
          color="bg-green-50"
          textColor="text-green-600"
        />
        <StatCard
          title="Top Request District"
          value={topRequestDistrict}
          icon="fa-fire"
          color="bg-orange-50"
          textColor="text-orange-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Donors by District</h2>
            <HorizontalBar
              items={(d.donorsByDistrict || []).slice(0, 15).map((i) => ({
                label: i.district,
                value: i.count,
              }))}
              color="bg-blue-500"
            />
          </div>
        </div>

        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Requests by District</h2>
            <HorizontalBar
              items={(d.requestsByDistrict || []).slice(0, 15).map((i) => ({
                label: i.district,
                value: i.count,
              }))}
              color="bg-red-500"
            />
          </div>
        </div>
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm">Top Areas by Donor Count</h2>
          <div className="overflow-x-auto">
            <table className="table table-zebra table-sm">
              <thead>
                <tr>
                  <th>District</th>
                  <th>Area</th>
                  <th>Donors</th>
                </tr>
              </thead>
              <tbody>
                {(d.topAreas || []).map((a, i) => (
                  <tr key={i}>
                    <td className="text-sm">{a.district}</td>
                    <td className="text-sm">{a.area}</td>
                    <td className="text-sm font-medium">{a.count}</td>
                  </tr>
                ))}
                {(!d.topAreas || d.topAreas.length === 0) && (
                  <tr>
                    <td colSpan={3} className="text-center text-gray-400">
                      No area data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {d.fulfillByDistrict && d.fulfillByDistrict.length > 0 && (
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Request Fulfillment by District</h2>
            <div className="overflow-x-auto">
              <table className="table table-zebra table-sm">
                <thead>
                  <tr>
                    <th>District</th>
                    <th>Open</th>
                    <th>Fulfilled</th>
                    <th>Cancelled</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {d.fulfillByDistrict.map((f, i) => (
                    <tr key={i}>
                      <td className="text-sm">{f.district}</td>
                      <td className="text-sm text-yellow-600">{f.open}</td>
                      <td className="text-sm text-green-600">{f.fulfilled}</td>
                      <td className="text-sm text-red-600">{f.cancelled}</td>
                      <td className="text-sm font-medium">{f.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationAnalyticsTab;
